import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import ContentText from '../Common/ContentText';
import './CommunitySelection.css';

interface Community {
  id: string;
  name: string;
  description: string;
  personalityTraits: string[];
  feature: {
    name: string;
    description: string;
    mechanics?: any;
  };
  tags: string[];
  source: string;
}

interface CommunitySelectionProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

const CommunitySelection: React.FC<CommunitySelectionProps> = ({
  characterData,
  onDataChange,
}) => {
  const [communities, setCommunities] = useState<Community[]>([]);
  const [selectedCommunity, setSelectedCommunity] = useState<Community | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCommunities = async () => {
      try {
        // Import the JSON file directly
        const communitiesData = await import('../../data/communities.json');
        setCommunities(communitiesData.communities);
        
        // If character already has a community selected, find and set it
        if (characterData.community) {
          const found = communitiesData.communities.find((c: Community) => c.id === characterData.community);
          if (found) {
            setSelectedCommunity(found);
          }
        }
      } catch (err) {
        console.error('Failed to load communities:', err);
        setError('Failed to load community data');
      } finally {
        setLoading(false);
      }
    };

    loadCommunities();
  }, [characterData.community]);

  const handleCommunitySelect = (community: Community) => {
    setSelectedCommunity(community);
    onDataChange({ community: community.id });
  };

  if (loading) {
    return <div className="community-selection__loading">Loading communities...</div>;
  }

  if (error) {
    return <div className="community-selection__error">{error}</div>;
  }

  return (
    <div className="community-selection">
      <p className="community-selection__description">
        Your community represents the type of society you grew up in. Each community provides
        unique personality traits, special features, and cultural background for your character.
      </p>

      <div className="community-selection__content">
        {/* Community List */}
        <div className="community-selection__list">
          <h3>Available Communities</h3>
          <div className="community-grid">
            {communities.map((community) => (
              <div
                key={community.id}
                className={`community-card ${
                  selectedCommunity?.id === community.id ? 'community-card--selected' : ''
                }`}
                onClick={() => handleCommunitySelect(community)}
              >
                <div className="community-card__name">{community.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Community Details */}
        {selectedCommunity && (
          <div className="community-selection__details">
            <div className="community-details">
              <h3>{selectedCommunity.name}</h3>
              <ContentText 
                content={selectedCommunity.description}
                className="community-details__description"
              />

              <div className="community-details__info">
                <div className="info-section">
                  <h4>Personality Traits</h4>
                  <div className="personality-traits">
                    {selectedCommunity.personalityTraits.map((trait, index) => (
                      <span key={index} className="personality-trait">
                        {trait}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="info-section">
                  <h4>Community Feature</h4>
                  <div className="feature-item">
                    <h5>{selectedCommunity.feature.name}</h5>
                    <ContentText content={selectedCommunity.feature.description} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunitySelection;